import { useEffect, useState } from 'react';

import { convertToRaw } from 'draft-js';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Avatar } from '@components/avatar';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconEmail } from '@components/icons/email';
import { IconFile } from '@components/icons/file';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconMessages } from '@components/icons/messages';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextEditor, useEditorState } from '@components/text-editor';
import { placeholdersToBrackets } from '@components/text-editor/plugin-placeholder';
import { useIsMobile } from '@hooks/use-is-mobile';
import { editorStateToHtml, draftToHtml } from '@utils/draftjs';

import { sortBy } from 'helpers';

import { useEmailTemplate } from 'api/companies/hooks';
import { Box } from 'components/Box';
import { TextEditorToolbar } from 'components/TextEditorToolbar';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { usePlaceholderMap } from 'hooks/use-placeholder-map';
import { useStore } from 'models/Provider';

import { useMessageTemplateSidebar } from './message-template';

export const ApplicationMessages = observer(({ application }) => {
  const { t } = useStore();

  useEffect(() => {
    application.setUserRead('messages');
    application.setUserRead('messagesFailed');
  }, [application]);

  return (
    <Stack space={4} style={{ marginTop: '1.5rem' }}>
      <Text font="bold14" text={t('application.messages.header')} />

      <div>
        <ApplicationMessageEditor application={application} />
        <Messages application={application} />
      </div>
    </Stack>
  );
});
const Messages = observer(({ application }) => {
  const messages = sortBy(application.messages, 'modified', 'desc');
  return messages.map((m) => <Message key={m.id} message={m} application={application} />);
});

const Message = observer(({ message, application }) => {
  if (message.messageType === 'announcement') {
    return <SystemMessage message={message} />;
  } else if (message.messageType === 'message') {
    return <UserMessage message={message} application={application} />;
  } else if (message.messageType === 'rejection') {
    return <RejectionMessage message={message} application={application} />;
  }
  return null;
});

const ApplicationMessageEditor = observer(({ application }) => {
  const { t } = useStore();
  const { showModal } = useModal();
  const { editorState, resetEditorState, isEmpty, setEditorState } = useEditorState();
  const [saving, setSaving] = useState(false);
  const placeholderMap = usePlaceholderMap('message');
  const { hasRolePermission } = usePermissions();
  const { data: emailTemplate } = useEmailTemplate();
  const canCreate = hasRolePermission('application:create-message-template');
  const confirm = () => {
    showModal('CUSTOM_CONFIRM', {
      _container: {
        autoWidth: true,
      },
      isHtml: true,
      data: {
        text: (
          <>
            {t('application.messages.confirm', application.name)}
            <Box
              border
              mt="24"
              padding="16"
              dangerouslySetInnerHTML={{ __html: editorStateToHtml(editorState) }}
            />
          </>
        ),
      },
      confirm: saveMessage,
    });
  };

  const saveMessage = async () => {
    setSaving(true);
    const rawData = convertToRaw(editorState.getCurrentContent());
    const converted = placeholdersToBrackets(rawData);
    const res = await application.sendMessage(draftToHtml(converted));
    setSaving(false);
    if (!res.error) {
      resetEditorState();
      return true;
    }
    return false;
  };
  const openMessageSidebar = useMessageTemplateSidebar();
  if (!hasRolePermission('application:use-messages')) {
    return (
      <>
        <Card variant="gray" space={4}>
          <Stack space={2} orientation="horizontal">
            <IconMessages color="purple80" />
            <Text color="purple60"> {t('application.messages.disabled.card')}</Text>
          </Stack>
        </Card>
      </>
    );
  } else
    return (
      <TextEditor
        variant="white"
        disabled={saving}
        editorState={editorState}
        onChange={setEditorState}
      >
        <ToolbarContainer>
          <TextEditorToolbar />
          <Stack space={2} orientation="horizontal" align="center">
            {emailTemplate && (
              <TextEditor.Template
                canCreate={canCreate}
                templates={emailTemplate}
                onSelect={() => {
                  openMessageSidebar();
                }}
              />
            )}
            <TextEditor.PlaceholderMenu
              label={t('editor.placeholder.title')}
              placeholderMap={placeholderMap}
            />
          </Stack>
        </ToolbarContainer>
        <TextEditor.Input placeholder={t('application.messages.editor.placeholder')} />
        <TextEditor.Actions>
          <Button
            data-cy="message-send"
            isDisabled={isEmpty || saving}
            onClick={confirm}
            text={t('application.messages.send')}
          />
        </TextEditor.Actions>
      </TextEditor>
    );
});

const ToolbarContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
  align: 'center',
  space: '2',
})({
  '@media(max-width: 768px)': {
    display: 'block',
    '[data-c="placeholder-trigger"]': {
      display: 'none',
    },
  },
});

// Full-width "conversation event" band shared by system announcements and
// rejections. They differ only by bubble variant and footer color/label.
const SystemBubble = observer(({ message, variant, iconColor, textColor, label, dataCy }) => {
  const { t } = useStore();
  return (
    <Box mt="32" data-cy={dataCy}>
      <Box flex flexDirection="column" alignItems="flex-end">
        <ReadOnlyMessageTextEditor html={message.message} variant={variant} />
        <MessageFail message={message} reverse />
        <Box flex alignItems="center" mt="8" gap="4">
          <IconEmail size={16} color={iconColor} />
          <Text font="regular14" color={textColor}>
            {t(label)} &bull; {message.dateDisplay}
          </Text>
        </Box>
      </Box>
    </Box>
  );
});

const SystemMessage = ({ message }) => (
  <SystemBubble
    message={message}
    variant="gray"
    iconColor="elementPurple50"
    textColor="textSecondary"
    label="application.messages.footer.application"
    dataCy="system-message"
  />
);

const RejectionMessage = ({ message }) => (
  <SystemBubble
    message={message}
    variant="red"
    iconColor="elementRed100"
    textColor="elementRed100"
    label="application.messages.footer.rejection"
    dataCy="rejection-message"
  />
);

const UserMessage = observer(({ message, application }) => {
  const { t } = useStore();
  const isMobile = useIsMobile();
  const { showFullscreenModal } = useModal();
  if (!message.user) {
    return (
      <Box mt="32">
        <Box flex>
          <Box mr="16">
            <Avatar
              className="avatar"
              src={application.picture}
              name={application.name}
              size="lg"
            />
          </Box>
          <div data-cy="message-no-user">
            <ReadOnlyMessageTextEditor html={message.message} variant="white" />
            {message.attachments.length > 0 && (
              <Box mt="8">
                <Stack space={2} align="flex-start">
                  {message.attachments.map((att) => (
                    <Button
                      variant="secondary"
                      iconLeft={<IconFile />}
                      tooltip={t('application.files.hoveropen')}
                      key={att.id}
                      onClick={() => {
                        showFullscreenModal('FILE_PREVIEW', {
                          value: att.id,
                          attachments: message.attachments,
                        });
                      }}
                      name="open"
                      text={att.fileName.replace(/_\w+.pdf/, '.pdf')}
                    />
                  ))}
                </Stack>
              </Box>
            )}
            <Box mt={isMobile ? '4' : '8'}>
              <Text font="regular14" color="textSecondary">
                {application.name} &bull; {message.dateDisplay}
              </Text>
            </Box>
          </div>
        </Box>
      </Box>
    );
  }

  return (
    <Box mt="32">
      <Box flex justifyContent="flex-end">
        <Box flex flexDirection="column" alignItems="flex-end" data-cy="message-user">
          <ReadOnlyMessageTextEditor html={message.message} variant="blue" />
          <MessageFail message={message} reverse />
          <Box mt={isMobile ? '4' : '8'}>
            <Text font="regular14" color="textSecondary">
              {message.user.name} <span>&bull; {message.dateDisplay}</span>
            </Text>
          </Box>
        </Box>
        <Box ml="16">
          <Avatar
            className="avatar"
            src={message.user.picture}
            name={message.user.name}
            size="lg"
          />
        </Box>
      </Box>
    </Box>
  );
});

const MessageFail = ({ message, reverse = false }) => {
  const { t } = useStore();
  return (
    <>
      {message.failedStatus && (
        <Box mt="8" maxW="424px" right={reverse}>
          <Box
            flex
            alignItems="center"
            justifyContent={reverse ? 'flex-end' : 'flex-start'}
            gap="4"
          >
            <IconFormIndicatorError size={16} />
            <Text color="elementRed100" font="semibold12">
              {t('application.messages.deliveryfailure')}
            </Text>
          </Box>
          <Text color="elementRed100" font="regular12">
            {t('application.messages.error')} {message.failedStatus.code} • {message.failedReason}
          </Text>
          <Text color="elementRed100" font="regular12">
            {message.failedStatus.description}
          </Text>
        </Box>
      )}
    </>
  );
};

function ReadOnlyMessageTextEditor({ html, variant = 'white' }) {
  const { userStore } = useStore();
  return (
    <TextEditor variant={variant} initialEditorState={html} readOnly>
      <TextEditor.Input />
      <TextEditor.Mention getUser={(id) => userStore.items.get(id)} />
    </TextEditor>
  );
}
